/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type { LexicalEditor } from 'lexical'

import { $createCodeNode } from '@lexical/code'
import {
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
} from '@lexical/list'

import {
  $createHeadingNode,
  $createQuoteNode,
  HeadingTagType,
} from '@lexical/rich-text'
import { $setBlocksType } from '@lexical/selection'

import { $createParagraphNode, $getSelection, $isRangeSelection } from 'lexical'

import FormatListBulleted from '~icons/material-symbols/format-list-bulleted'
import FormatListNumbered from '~icons/material-symbols/format-list-numbered'
import FormatH1 from '~icons/material-symbols/format-h1'
import FormatH2 from '~icons/material-symbols/format-h2'
import FormatH3 from '~icons/material-symbols/format-h3'
import FormatQuote from '~icons/material-symbols/format-quote'
import Code from '~icons/material-symbols/code'
import FormatParagraph from '~icons/mdi/format-paragraph'
import CheckBoxOutline from '~icons/material-symbols/check-box-outline'

import { DropDownItem, ToolbarDropDown } from './toolbarDropDown'
import {
  blockTypeToBlockName,
  dropDownActiveClass,
  rootTypeToRootName,
} from '../plugins/toolbarPlugin'
import { useTranslation } from '@crew/modules/i18n'

// 引数のblockTypeによって、表示するアイコンを変える
const blockTypeToIcon = {
  paragraph: <FormatParagraph width={24} height={24} />,
  h1: <FormatH1 width={24} height={24} />,
  h2: <FormatH2 width={24} height={24} />,
  h3: <FormatH3 width={24} height={24} />,
  quote: <FormatQuote width={24} height={24} />,
  codeBlock: <Code width={24} height={24} />,
  bulletList: <FormatListBulleted width={24} height={24} />,
  checkList: <CheckBoxOutline width={24} height={24} />,
  numberList: <FormatListNumbered width={24} height={24} />,
}

export function BlockFormatDropDown({
  editor,
  blockType,
  rootType,
  disabled = false,
  tooltip,
  tooltipPosition,
}: {
  blockType: keyof typeof blockTypeToBlockName
  rootType: keyof typeof rootTypeToRootName
  editor: LexicalEditor
  disabled?: boolean
  tooltip?: string
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right'
}): JSX.Element {
  const { t } = useTranslation()

  const formatParagraph = () => {
    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode())
      }
    })
  }

  const formatHeading = (headingSize: HeadingTagType) => {
    if (blockType !== headingSize) {
      editor.update(() => {
        const selection = $getSelection()
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createHeadingNode(headingSize))
        }
      })
    }
  }

  const formatBulletList = () => {
    if (blockType !== 'bulletList') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
    } else {
      formatParagraph()
    }
  }

  const formatCheckList = () => {
    if (blockType !== 'checkList') {
      editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined)
    } else {
      formatParagraph()
    }
  }

  const formatNumberedList = () => {
    if (blockType !== 'numberList') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
    } else {
      formatParagraph()
    }
  }

  const formatQuote = () => {
    if (blockType !== 'quote') {
      editor.update(() => {
        const selection = $getSelection()
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createQuoteNode())
        }
      })
    }
  }

  const formatCode = () => {
    if (blockType !== 'codeBlock') {
      editor.update(() => {
        let selection = $getSelection()

        if (selection !== null) {
          if (selection.isCollapsed()) {
            $setBlocksType(selection, () => $createCodeNode())
          } else {
            const textContent = selection.getTextContent()
            const codeNode = $createCodeNode()
            selection.insertNodes([codeNode])
            selection = $getSelection()
            if ($isRangeSelection(selection)) {
              selection.insertRawText(textContent)
            }
          }
        }
      })
    }
  }

  return (
    <ToolbarDropDown
      disabled={disabled}
      // buttonClassName="toolbar-item block-controls"
      icon={blockTypeToIcon[blockType]}
      buttonAriaLabel="Formatting options for text style"
      showDropDownIcon={true}
      tooltip={tooltip}
      tooltipPosition={tooltipPosition}
    >
      <DropDownItem
        className={'item ' + dropDownActiveClass(blockType === 'paragraph')}
        onClick={formatParagraph}
      >
        <div className="flex justify-center items-center gap-x-1">
          <FormatParagraph width={24} height={24} />
          <span className="text">{t('action.lexical.paragraph')}</span>
        </div>
      </DropDownItem>
      <DropDownItem
        className={'item ' + dropDownActiveClass(blockType === 'h1')}
        onClick={() => formatHeading('h1')}
      >
        <div className="flex justify-center items-center gap-x-1">
          <FormatH1 width={24} height={24} />
          <span className="text">{t('action.lexical.h1')}</span>
        </div>
      </DropDownItem>
      <DropDownItem
        className={'item ' + dropDownActiveClass(blockType === 'h2')}
        onClick={() => formatHeading('h2')}
      >
        <div className="flex justify-center items-center gap-x-1">
          <FormatH2 width={24} height={24} />
          <span className="text">{t('action.lexical.h2')}</span>
        </div>
      </DropDownItem>
      <DropDownItem
        className={'item ' + dropDownActiveClass(blockType === 'h3')}
        onClick={() => formatHeading('h3')}
      >
        <div className="flex justify-center items-center gap-x-1">
          <FormatH3 width={24} height={24} />
          <span className="text">{t('action.lexical.h3')}</span>
        </div>
      </DropDownItem>
      <DropDownItem
        className={'item ' + dropDownActiveClass(blockType === 'bulletList')}
        onClick={formatBulletList}
      >
        <div className="flex justify-center items-center gap-x-1">
          <FormatListBulleted width={24} height={24} />
          <span className="text">{t('action.lexical.bulletList')}</span>
        </div>
      </DropDownItem>
      <DropDownItem
        className={'item ' + dropDownActiveClass(blockType === 'numberList')}
        onClick={formatNumberedList}
      >
        <div className="flex justify-center items-center gap-x-1">
          <FormatListNumbered width={24} height={24} />
          <span className="text">{t('action.lexical.numberList')}</span>
        </div>
      </DropDownItem>
      <DropDownItem
        className={'item ' + dropDownActiveClass(blockType === 'checkList')}
        onClick={formatCheckList}
      >
        <div className="flex justify-center items-center gap-x-1">
          <CheckBoxOutline width={24} height={24} />
          <span className="text">{t('action.lexical.checkList')}</span>
        </div>
      </DropDownItem>
      <DropDownItem
        className={'item ' + dropDownActiveClass(blockType === 'quote')}
        onClick={formatQuote}
      >
        <div className="flex justify-center items-center gap-x-1">
          <FormatQuote width={24} height={24} />
          <span className="text">{t('action.lexical.quote')}</span>
        </div>
      </DropDownItem>
      <DropDownItem
        className={'item ' + dropDownActiveClass(blockType === 'codeBlock')}
        onClick={formatCode}
      >
        <div className="flex justify-center items-center gap-x-1">
          <Code width={24} height={24} />
          <span className="text">{t('action.lexical.codeBlock')}</span>
        </div>
      </DropDownItem>
    </ToolbarDropDown>
  )
}
